import { useAtomValue } from "jotai";
import { FundAtom, SelectedFundAtom } from "@/store/FundStateAtom";
import {
  ReportingPeriodAtom,
  SelectedReportingPeriodAtom,
} from "@/store/ReportingPeriodStateAtom";
import { useState, useEffect } from "react";
import { Fund } from "@/types/Fund";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import {
  TransactionWithInstrument,
  TransactionWithInstrumentAndAccount,
} from "@/types/Transaction";
import {
  HoldingWithInstrument,
  HoldingWithInstrumentAndAccount,
} from "@/types/Holding";
import { RuleException } from "@/types/RuleException";
import { Import } from "@/types/Import";
import { getTransactions } from "@/api/Transactions";
import { getHoldings } from "@/api/Holdings";
import { getImports } from "@/api/Import";
import { getFundsNav } from "@/api/Fund";
import { getRuleExceptions } from "@/api/RuleExceptions";
import { getHistoricalNav } from "@/api/Analytics";
import { HistoricalNavResult } from "@/types";

// Entity upload type for checklist
interface EntityUpload {
  type: string;
  uploaded: boolean;
  lastUploaded?: string;
}

// Entity types we expect to be uploaded during a reporting period
const EXPECTED_ENTITY_TYPES = [
  "Transactions",
  "Holdings",
  // "Investors",
  // "Funds",
  // "Accounts",
  // "Instruments",
];

// Map import contexts to friendly entity type names
const IMPORT_CONTEXT_MAPPING: Record<string, string> = {
  transaction: "Transactions",
  holding: "Holdings",
  investor: "Investors",
  expense: "Fund Expenses",
  nav: "NAV Calculations",
  fee: "Fee Calculations",
};

// Define severity mapping for rule exceptions
const mapSeverityFromStatus = (status: string): "Low" | "Medium" | "High" | "Critical" => {
  // Map rule exception status to severity levels
  // This is a simple mapping - you may need to adjust based on your business logic
  switch (status.toLowerCase()) {
    case "critical":
    case "error":
      return "Critical";
    case "high":
    case "warning":
      return "High";
    case "medium":
    case "pending":
      return "Medium";
    case "low":
    case "info":
    default:
      return "Low";
  }
};

export interface FundDashboardController {
  selectedFund: Fund | undefined;
  selectedReportingPeriod: ReportingPeriod | undefined;
  exceptionCount: number;
  criticalExceptionCount: number;
  highExceptionCount: number;
  mediumExceptionCount: number;
  lowExceptionCount: number;
  criticalExceptionPercentage: number;
  highExceptionPercentage: number;
  exceptions: RuleException[];
  unconfirmedTransactions: TransactionWithInstrument[];
  confirmedTransactions: TransactionWithInstrument[];
  totalTransactions: number;
  unconfirmedHoldings: HoldingWithInstrument[];
  confirmedHoldings: HoldingWithInstrument[];
  totalHoldings: number;
  entityUploads: EntityUpload[];
  uploadCompletionPercentage: number;
  navData: any;
  historicalNavData: HistoricalNavResult[];
  isLoading: boolean;
}

/**
 * Controller hook for the FundDashboard component
 * Provides data and logic for the fund dashboard view
 */
export const useFundDashboardController = (): FundDashboardController => {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);

  // State for real data
  const [exceptions, setExceptions] = useState<RuleException[]>([]);
  const [transactions, setTransactions] = useState<
    TransactionWithInstrumentAndAccount[]
  >([]);
  const [holdings, setHoldings] = useState<HoldingWithInstrumentAndAccount[]>(
    []
  );
  const [imports, setImports] = useState<Import[]>([]);
  const [entityUploads, setEntityUploads] = useState<EntityUpload[]>([]);
  const [navData, setNavData] = useState<any>(null);
  const [historicalNavData, setHistoricalNavData] = useState<HistoricalNavResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch real data when fund and reporting period are selected
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedFund || !selectedReportingPeriod) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch transactions for the selected fund
        const transactionsData = await getTransactions(
          selectedFund.fundId,
          parseInt(selectedReportingPeriod.reportingPeriodId)
        );

        // Fetch holdings for the selected fund
        const holdingsData = await getHoldings(
          selectedFund.fundId,
          parseInt(selectedReportingPeriod.reportingPeriodId)
        );

        // Fetch imports
        const importsData = await getImports();

        // Fetch NAV data
        const navData = await getFundsNav(
          parseInt(selectedReportingPeriod.reportingPeriodId)
        );

        // Fetch rule exceptions for the selected reporting period
        const exceptionsData = await getRuleExceptions(
          parseInt(selectedReportingPeriod.reportingPeriodId)
        );

        // Fetch historical NAV data for the selected fund
        const historicalNavData = await getHistoricalNav(selectedFund.fundId);

        // Store the fetched data
        setTransactions(transactionsData);
        setHoldings(holdingsData);
        setImports(importsData);
        console.log({importsData})
        setNavData(navData);
        setExceptions(exceptionsData);
        setHistoricalNavData(historicalNavData);

        // Process imports to determine what entities have been uploaded for this period
        processEntityUploads(importsData, selectedReportingPeriod);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedFund, selectedReportingPeriod]);

  // Process imports to determine what entities have been uploaded for this period
  const processEntityUploads = (
    imports: Import[],
    reportingPeriod: ReportingPeriod
  ) => {
    // Create a map to track the latest upload date for each entity type
    const uploadMap = new Map<string, string>();

    // Get reporting period start date (we'll consider this to be the period string)
    const reportingPeriodDate = new Date(reportingPeriod.period);

    // Find imports that were created after the reporting period start date
    const periodImports = imports.filter((imp) => {
      const importDate = new Date(imp.createdAt);
     
      return (
        (importDate >= reportingPeriodDate && imp.status === "Finished" ) ||
        imp.status === "FinishedWithErrors"
      );
    });

    // Extract entity types from import contexts and track the latest upload date
    periodImports.forEach((imp) => {
      // Map the import context to our entity type
      // This is a simple approach - in reality, you might need more complex logic
      const entityType = determineEntityType(imp.context);

      if (entityType) {
        const currentLatest = uploadMap.get(entityType);
        if (
          !currentLatest ||
          new Date(imp.createdAt) > new Date(currentLatest)
        ) {
          uploadMap.set(entityType, imp.createdAt);
        }
      }
    });

    // Create our entity uploads list
    const uploads: EntityUpload[] = EXPECTED_ENTITY_TYPES.map((type) => ({
      type,
      uploaded: uploadMap.has(type),
      lastUploaded: uploadMap.get(type),
    }));

    setEntityUploads(uploads);
  };

  // Helper to map import context to entity type
  const determineEntityType = (context: string): string | null => {
    // Attempt to match the context with our mapping
    for (const [key, value] of Object.entries(IMPORT_CONTEXT_MAPPING)) {
      if (context.toLowerCase().includes(key.toLowerCase())) {
        return value;
      }
    }
    return null;
  };

  // Filter for unconfirmed transactions and holdings
  const unconfirmedTransactions = transactions
    .filter((t) => t.isConfirmed === false)
    .map((t) => t as unknown as TransactionWithInstrument);

  const confirmedTransactions = transactions
    .filter((t) => t.isConfirmed === true)
    .map((t) => t as unknown as TransactionWithInstrument);

  const totalTransactions = transactions.length;

  const unconfirmedHoldings = holdings
    .filter((h) => h.isConfirmed === false)
    .map((h) => h as unknown as HoldingWithInstrument);

  const confirmedHoldings = holdings
    .filter((h) => h.isConfirmed === true)
    .map((h) => h as unknown as HoldingWithInstrument);

  const totalHoldings = holdings.length;

  // Calculate exception counts based on risk level (use riskLevel field directly)
  const exceptionCount = exceptions.length;
  const criticalExceptionCount = exceptions.filter(
    (e) => e.riskLevel === "Critical"
  ).length;
  const highExceptionCount = exceptions.filter(
    (e) => e.riskLevel === "High"
  ).length;
  const mediumExceptionCount = exceptions.filter(
    (e) => e.riskLevel === "Medium"
  ).length;
  const lowExceptionCount = exceptions.filter(
    (e) => e.riskLevel === "Low"
  ).length;

  // Calculate exception percentages for progress bars
  const criticalExceptionPercentage =
    exceptionCount > 0 ? (criticalExceptionCount / exceptionCount) * 100 : 0;
  const highExceptionPercentage =
    exceptionCount > 0 ? (highExceptionCount / exceptionCount) * 100 : 0;

  // Calculate upload completion percentage
  const uploadCompletionPercentage =
    entityUploads.length > 0
      ? Math.round(
          (entityUploads.filter((item) => item.uploaded).length /
            entityUploads.length) *
            100
        )
      : 0;

  return {
    selectedFund,
    selectedReportingPeriod,
    exceptionCount,
    criticalExceptionCount,
    highExceptionCount,
    mediumExceptionCount,
    lowExceptionCount,
    criticalExceptionPercentage,
    highExceptionPercentage,
    exceptions,
    unconfirmedTransactions,
    confirmedTransactions,
    totalTransactions,
    unconfirmedHoldings,
    confirmedHoldings,
    totalHoldings,
    entityUploads,
    uploadCompletionPercentage,
    navData,
    historicalNavData,
    isLoading,
  };
};
